import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Table,
  Tag,
  Space,
  Input,
  Select,
  DatePicker,
  Switch,
  Descriptions,
  Alert,
  Button,
} from 'antd';
import { FileSearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAuditLogs } from '../../hooks/useAuditLogs';
import { AuditLogEntry } from '../../services/audit.service';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const ACTION_COLORS: Record<string, string> = {
  create: 'green',
  update: 'blue',
  delete: 'red',
  login: 'cyan',
  logout: 'default',
  export: 'purple',
  import: 'orange',
};

export const AuditLog: React.FC = () => {
  const [autoRefresh, setAutoRefresh] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const {
    logs,
    loading,
    error,
    meta,
    filters,
    setFilters,
    refetch,
  } = useAuditLogs({ page: 1, limit: 20 });

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => {
        refetch();
      }, 30000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoRefresh, refetch]);

  const handleDateChange = (dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      setFilters({
        ...filters,
        dateFrom: dates[0].format('YYYY-MM-DD'),
        dateTo: dates[1].format('YYYY-MM-DD'),
        page: 1,
      });
    } else {
      setFilters({
        ...filters,
        dateFrom: undefined,
        dateTo: undefined,
        page: 1,
      });
    }
  };

  const handleActorSearch = (value: string) => {
    setFilters({ ...filters, actor: value || undefined, page: 1 });
  };

  const handleActionFilter = (value: string) => {
    setFilters({ ...filters, action: value || undefined, page: 1 });
  };

  const handlePageChange = (page: number, pageSize: number) => {
    setFilters({ ...filters, page, limit: pageSize });
  };

  const columns = [
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 180,
      render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: 'Actor',
      dataIndex: 'actor',
      key: 'actor',
      width: 200,
      render: (actor: AuditLogEntry['actor']) => (
        <div>
          <Text strong>{actor.name}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>{actor.email}</Text>
        </div>
      ),
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 120,
      render: (action: string) => (
        <Tag color={ACTION_COLORS[action] || 'default'}>
          {action.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Target',
      dataIndex: 'target',
      key: 'target',
      width: 200,
      render: (target: AuditLogEntry['target']) => (
        <Space>
          <Tag>{target.type}</Tag>
          <Text copyable={{ text: target.id }} style={{ fontSize: 12 }}>
            {target.id.substring(0, 8)}...
          </Text>
        </Space>
      ),
    },
    {
      title: 'IP Address',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      width: 140,
    },
    {
      title: 'User Agent',
      dataIndex: 'userAgent',
      key: 'userAgent',
      ellipsis: true,
      render: (v: string) => (
        <Text style={{ fontSize: 12 }} title={v}>
          {v.length > 40 ? `${v.substring(0, 40)}...` : v}
        </Text>
      ),
    },
  ];

  const expandedRowRender = (record: AuditLogEntry) => (
    <Descriptions size="small" column={1} bordered>
      <Descriptions.Item label="Full Details">
        <pre style={{ margin: 0, fontSize: 12, maxHeight: 200, overflow: 'auto' }}>
          {JSON.stringify(record.details, null, 2)}
        </pre>
      </Descriptions.Item>
      <Descriptions.Item label="Full User Agent">
        {record.userAgent}
      </Descriptions.Item>
      <Descriptions.Item label="Target ID">
        {record.target.id}
      </Descriptions.Item>
    </Descriptions>
  );

  if (error) {
    return (
      <Alert
        message="Lỗi tải dữ liệu"
        description={error.message || 'Vui lòng thử lại sau'}
        type="error"
        showIcon
        action={<Button onClick={refetch}>Thử lại</Button>}
      />
    );
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Space>
            <FileSearchOutlined style={{ fontSize: 24 }} />
            <Title level={3} style={{ margin: 0 }}>Audit Log</Title>
          </Space>
        </Col>
        <Col>
          <Space>
            <Text>Auto-refresh (30s):</Text>
            <Switch checked={autoRefresh} onChange={setAutoRefresh} />
          </Space>
        </Col>
      </Row>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col span={8}>
            <RangePicker
              style={{ width: '100%' }}
              onChange={handleDateChange}
              placeholder={['Date from', 'Date to']}
            />
          </Col>
          <Col span={8}>
            <Input.Search
              placeholder="Search by actor name or email"
              onSearch={handleActorSearch}
              allowClear
            />
          </Col>
          <Col span={8}>
            <Select
              placeholder="Filter by action"
              style={{ width: '100%' }}
              onChange={handleActionFilter}
              allowClear
            >
              <Option value="create">Create</Option>
              <Option value="update">Update</Option>
              <Option value="delete">Delete</Option>
              <Option value="login">Login</Option>
              <Option value="logout">Logout</Option>
              <Option value="export">Export</Option>
              <Option value="import">Import</Option>
            </Select>
          </Col>
        </Row>
      </Card>

      <Table
        columns={columns}
        dataSource={logs}
        loading={loading}
        rowKey="id"
        expandable={{ expandedRowRender }}
        pagination={{
          current: meta?.page || 1,
          pageSize: meta?.limit || 20,
          total: meta?.total || 0,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} entries`,
          onChange: handlePageChange,
        }}
      />
    </div>
  );
};

export default AuditLog;
